import { inject, Injectable } from "@angular/core";
import { School } from "../models/school.model";
import { District } from "../models/district.model";
import { map, Observable, of } from "rxjs";
import { environment } from "src/environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { professionalLearningIntroSteps } from "src/app/resources/professional-learning.intro-steps";

export interface StatSummary {
  readonly districtCount: number;
  readonly schoolCount: number;
  readonly teacherCount: number;
  readonly studentCount: number;
  readonly onboardedPercent: number;
  readonly loginsPercent: number;
}

export interface SchoolHealth {
  school: School;
  district: District;
  teacherCount: number;
  avgWeeklyTeacherLoginsMax2: number;
  teachersWhoLoggedInOnceWithinLastWeekCount: number;
  studentCount: number;
  avgWeeklyStudentSessionTimeMax60: number;
  managementUserAttendance: number;
  hasWeeklySchoolUserLogin: boolean;
  onboarded: boolean;
  prevMonthHealth: null | {
    avgWeeklyTeacherLoginsMax2: number;
    avgWeeklyStudentSessionTimeMax60: number;
    managementUserAttendance: number;
    hasWeeklySchoolUserLogin: boolean;
  }
}

export interface SchoolHealthScore {
  school: School;
  district: District;
  resourceHealth: number;
  professionalLearningHealth: number;
  loginHealth: number;
  studentUsageHealth: number;
  overallHealth: number;
  overallHealthChangeFromPrevMonth?: number;
  teacherCount: number;
  teachersWhoLoggedInOnceWithinLastWeekCount: number;
  studentCount: number;
  onboarded: boolean;
}

export const HEALTH_SCORE_WEIGHTS = {
  STUDENT_USAGE: 40,
  TEACHER_LOGIN: 20,
  SCHOOL_USER_LOGIN: 20,
  PROFESSIONAL_LEARNING_ATTENDANCE: 20,
};


@Injectable({
  providedIn: 'root'
})
export class PartnerSuccessService {
  private readonly http = inject(HttpClient);

  getStatSummary(districtID: number, schoolID: number): Observable<StatSummary> {
    return of({
      districtCount: 23,
      schoolCount: 95,
      teacherCount: 128,
      studentCount: 10984,
      onboardedPercent: 83,
      loginsPercent: 75,
    });
  }

  getSchoolHealth(districtID: number, schoolID: number): Observable<SchoolHealthScore[]> {
    let params = new HttpParams();
    if (districtID) {
      params = params.append('districtID', districtID)
    }
    if (schoolID) {
      params = params.append('schoolID', schoolID)
    }
    return this.http
      .get<SchoolHealth[]>(
        `${environment.dataServicesURL}manage/reports/health`,
        { withCredentials: true, params }
       ).pipe(
        map(
          schools => schools.map(
            ({
              school,
              district,
              teacherCount,
              teachersWhoLoggedInOnceWithinLastWeekCount,
              avgWeeklyTeacherLoginsMax2,
              studentCount,
              avgWeeklyStudentSessionTimeMax60,
              managementUserAttendance,
              hasWeeklySchoolUserLogin,
              onboarded,
              prevMonthHealth,
            }) => {
              const loginHealth = Math.round(HEALTH_SCORE_WEIGHTS.TEACHER_LOGIN * avgWeeklyTeacherLoginsMax2 / 2);
              const studentUsageHealth = Math.round(HEALTH_SCORE_WEIGHTS.STUDENT_USAGE * avgWeeklyStudentSessionTimeMax60 / 60);
              const resourceHealth = hasWeeklySchoolUserLogin ? HEALTH_SCORE_WEIGHTS.SCHOOL_USER_LOGIN : 0;
              const professionalLearningHealth = Math.round(managementUserAttendance * HEALTH_SCORE_WEIGHTS.PROFESSIONAL_LEARNING_ATTENDANCE);

              const overallHealth = loginHealth + studentUsageHealth + resourceHealth + professionalLearningHealth;
              const prevMonthOverallHealth = prevMonthHealth ? (
                 Math.round(HEALTH_SCORE_WEIGHTS.TEACHER_LOGIN * prevMonthHealth.avgWeeklyTeacherLoginsMax2 / 2) +
                 Math.round(HEALTH_SCORE_WEIGHTS.STUDENT_USAGE * prevMonthHealth.avgWeeklyStudentSessionTimeMax60 / 60) +
                 (prevMonthHealth.hasWeeklySchoolUserLogin ? HEALTH_SCORE_WEIGHTS.SCHOOL_USER_LOGIN : 0) +
                 Math.round(prevMonthHealth.managementUserAttendance * HEALTH_SCORE_WEIGHTS.PROFESSIONAL_LEARNING_ATTENDANCE)
              ) : undefined;

              return {
                school,
                district,
                teacherCount,
                teachersWhoLoggedInOnceWithinLastWeekCount,
                studentCount,
                professionalLearningHealth,
                loginHealth,
                studentUsageHealth,
                resourceHealth,
                overallHealth,
                overallHealthChangeFromPrevMonth: prevMonthOverallHealth ? (overallHealth - prevMonthOverallHealth) : undefined,
                onboarded,
              }
            }
          )
      )
    );
  }
}
